import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { settingsAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import AdminSidebar from '../../components/AdminSidebar';
import LanguageToggle from '../../components/LanguageToggle';
import { 
  Settings as SettingsIcon, 
  Save, 
  Building2, 
  Mail, 
  Clock, 
  DollarSign,
  Truck,
  Package,
  Palette,
  RefreshCw,
  CheckCircle,
  Search,
  Share2,
  Bell,
  Shield,
  Globe,
  Image
} from 'lucide-react';
import '../Admin/AdminDashboard.css';
import './AdminSettings.css';

const AdminSettings = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('business_info');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [dirtyTabs, setDirtyTabs] = useState(new Set());
  const [settings, setSettings] = useState({
    business_info: {
      name: '',
      description: '',
      vatNumber: '',
      address: {
        street: '',
        city: '',
        postalCode: '',
        country: 'Italy'
      }
    },
    contact: {
      email: '',
      phone: '',
      whatsapp: '',
      supportEmail: ''
    },
    hours: {
      monday: { open: '09:00', close: '18:00', closed: false },
      tuesday: { open: '09:00', close: '18:00', closed: false },
      wednesday: { open: '09:00', close: '18:00', closed: false },
      thursday: { open: '09:00', close: '18:00', closed: false },
      friday: { open: '09:00', close: '18:00', closed: false },
      saturday: { open: '09:00', close: '13:00', closed: false },
      sunday: { open: '', close: '', closed: true }
    },
    pricing: {
      baseRate: 0,
      urgentSurcharge: 50,
      minOrder: 20,
      taxRate: 22
    },
    shipping: {
      freeThreshold: 100,
      standardRate: 10,
      expressRate: 25,
      localPickup: true
    },
    materials: [],
    seo: {
      metaTitle: '',
      metaDescription: '',
      keywords: [],
      ogImage: '',
      googleAnalyticsId: '',
      facebookPixelId: '',
      googleTagManagerId: ''
    },
    branding: {
      primaryColor: '#10b981',
      secondaryColor: '#3b82f6',
      accentColor: '#f59e0b',
      logoUrl: '',
      faviconUrl: '',
      companyTagline: ''
    },
    notifications: {
      emailOnNewLead: true,
      emailOnNewOrder: true,
      smsOnUrgentOrder: false,
      notificationEmail: '',
      slackWebhookUrl: ''
    },
    security: {
      twoFactorEnabled: false,
      sessionTimeout: 480,
      ipWhitelist: [],
      allowedDomains: []
    },
    integrations: {
      stripePublicKey: '',
      paypalClientId: '',
      whatsappBusinessId: '',
      twilioAccountSid: ''
    }
  });

  const tabs = [
    { id: 'business_info', label: t('admin.settings.tabs.businessInfo'), icon: Building2, color: '#3b82f6' },
    { id: 'contact', label: t('admin.settings.tabs.contact'), icon: Mail, color: '#10b981' },
    { id: 'hours', label: t('admin.settings.tabs.hours'), icon: Clock, color: '#f59e0b' },
    { id: 'pricing', label: t('admin.settings.tabs.pricing'), icon: DollarSign, color: '#ec4899' },
    { id: 'shipping', label: t('admin.settings.tabs.shipping'), icon: Truck, color: '#8b5cf6' },
    { id: 'materials', label: t('admin.settings.tabs.materials'), icon: Package, color: '#14b8a6' },
    { id: 'seo', label: t('admin.settings.tabs.seo'), icon: Search, color: '#06b6d4' },
    { id: 'branding', label: t('admin.settings.tabs.branding'), icon: Palette, color: '#ec4899' },
    { id: 'notifications', label: t('admin.settings.tabs.notifications'), icon: Bell, color: '#f59e0b' },
    { id: 'security', label: t('admin.settings.tabs.security'), icon: Shield, color: '#ef4444' },
    { id: 'integrations', label: t('admin.settings.tabs.integrations'), icon: Share2, color: '#8b5cf6' }
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await settingsAPI.getAll();
      if (response.data.settings && response.data.settings.length > 0) {
        const settingsData = {};
        response.data.settings.forEach(item => {
          let value = item.settings;
          // AdminProducts saves materials as { materials: [...] } — unwrap to keep a consistent array shape
          if (item.category === 'materials' && value && !Array.isArray(value) && Array.isArray(value.materials)) {
            value = value.materials;
          }
          settingsData[item.category] = value;
        });
        setSettings(prev => ({ ...prev, ...settingsData }));
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const markDirty = () => {
    setDirtyTabs(prev => new Set([...prev, activeTab]));
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccessMessage('');
    try {
      await settingsAPI.update(activeTab, settings[activeTab]);
      setSuccessMessage(t('admin.settings.saveSuccess'));
      setTimeout(() => setSuccessMessage(''), 3000);
      setDirtyTabs(prev => { const next = new Set(prev); next.delete(activeTab); return next; });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(t('admin.settings.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (category, field, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
    markDirty();
  };

  const updateNestedSetting = (category, parent, field, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [parent]: {
          ...prev[category][parent],
          [field]: value
        }
      }
    }));
    markDirty();
  };

  const addMaterial = () => {
    setSettings(prev => ({
      ...prev,
      materials: [
        ...prev.materials,
        {
          name: '',
          pricePerGram: 0,
          colors: [],
          available: true
        }
      ]
    }));
    markDirty();
  };

  const updateMaterial = (index, field, value) => {
    setSettings(prev => ({
      ...prev,
      materials: prev.materials.map((mat, i) => 
        i === index ? { ...mat, [field]: value } : mat
      )
    }));
    markDirty();
  };

  const removeMaterial = (index) => {
    setSettings(prev => ({
      ...prev,
      materials: prev.materials.filter((_, i) => i !== index)
    }));
    markDirty();
  };

  const addKeyword = () => {
    setSettings(prev => ({
      ...prev,
      seo: {
        ...prev.seo,
        keywords: [...prev.seo.keywords, '']
      }
    }));
    markDirty();
  };

  const updateKeyword = (index, value) => {
    setSettings(prev => ({
      ...prev,
      seo: {
        ...prev.seo,
        keywords: prev.seo.keywords.map((kw, i) => i === index ? value : kw)
      }
    }));
    markDirty();
  };

  const removeKeyword = (index) => {
    setSettings(prev => ({
      ...prev,
      seo: {
        ...prev.seo,
        keywords: prev.seo.keywords.filter((_, i) => i !== index)
      }
    }));
    markDirty();
  };

  const addToList = (category, field, value = '') => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: [...(prev[category][field] || []), value]
      }
    }));
    markDirty();
  };

  const updateListItem = (category, field, index, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: prev[category][field].map((item, i) => i === index ? value : item)
      }
    }));
    markDirty();
  };

  const removeFromList = (category, field, index) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: prev[category][field].filter((_, i) => i !== index)
      }
    }));
    markDirty();
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'business_info':
        return (
          <div className="settings-section">
            <div className="form-group">
              <label>{t('admin.settings.businessName')}</label>
              <input
                type="text"
                value={settings.business_info.name}
                onChange={(e) => updateSetting('business_info', 'name', e.target.value)}
                placeholder="MeccaFlex"
              />
            </div>

            <div className="form-group">
              <label>{t('admin.settings.description')}</label>
              <textarea
                value={settings.business_info.description}
                onChange={(e) => updateSetting('business_info', 'description', e.target.value)}
                placeholder="Stampa 3D industriale per aziende"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>{t('admin.settings.vatNumber')}</label>
              <input
                type="text"
                value={settings.business_info.vatNumber}
                onChange={(e) => updateSetting('business_info', 'vatNumber', e.target.value)}
                placeholder="IT12345678901"
              />
            </div>

            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>{t('admin.settings.address')}</h3>

            <div className="form-group">
              <label>{t('admin.settings.street')}</label>
              <input
                type="text"
                value={settings.business_info.address?.street || ''}
                onChange={(e) => updateNestedSetting('business_info', 'address', 'street', e.target.value)}
                placeholder="Via Roma 123"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.settings.city')}</label>
                <input
                  type="text"
                  value={settings.business_info.address?.city || ''}
                  onChange={(e) => updateNestedSetting('business_info', 'address', 'city', e.target.value)}
                  placeholder="Milano"
                />
              </div>

              <div className="form-group">
                <label>{t('admin.settings.postalCode')}</label>
                <input
                  type="text"
                  value={settings.business_info.address?.postalCode || ''}
                  onChange={(e) => updateNestedSetting('business_info', 'address', 'postalCode', e.target.value)}
                  placeholder="20100"
                />
              </div>

              <div className="form-group">
                <label>{t('admin.settings.country')}</label>
                <input
                  type="text"
                  value={settings.business_info.address?.country || 'Italy'}
                  onChange={(e) => updateNestedSetting('business_info', 'address', 'country', e.target.value)}
                />
              </div>
            </div>
          </div>
        );

      case 'contact':
        return (
          <div className="settings-section">
            <div className="form-group">
              <label>{t('admin.settings.mainEmail')}</label>
              <input
                type="email"
                value={settings.contact.email}
                onChange={(e) => updateSetting('contact', 'email', e.target.value)}
                placeholder="info@meccaflex.com"
              />
            </div>

            <div className="form-group">
              <label>{t('admin.settings.phone')}</label>
              <input
                type="tel"
                value={settings.contact.phone}
                onChange={(e) => updateSetting('contact', 'phone', e.target.value)}
                placeholder="+39 123 456 7890"
              />
            </div>

            <div className="form-group">
              <label>{t('admin.settings.whatsapp')}</label>
              <input
                type="tel"
                value={settings.contact.whatsapp}
                onChange={(e) => updateSetting('contact', 'whatsapp', e.target.value)}
                placeholder="+39 123 456 7890"
              />
            </div>

            <div className="form-group">
              <label>{t('admin.settings.supportEmail')}</label>
              <input
                type="email"
                value={settings.contact.supportEmail}
                onChange={(e) => updateSetting('contact', 'supportEmail', e.target.value)}
                placeholder="support@meccaflex.com"
              />
            </div>
          </div>
        );

      case 'hours':
        return (
          <div className="settings-section">
            {Object.entries(settings.hours).map(([day, hours]) => (
              <div key={day} className="hours-row">
                <div className="day-name">
                  {day.charAt(0).toUpperCase() + day.slice(1)}
                </div>
                <div className="hours-inputs">
                  <input
                    type="checkbox"
                    checked={!hours.closed}
                    onChange={(e) => updateNestedSetting('hours', day, 'closed', !e.target.checked)}
                    id={`${day}-open`}
                  />
                  <label htmlFor={`${day}-open`}>{t('admin.settings.open')}</label>
                  
                  {!hours.closed && (
                    <>
                      <input
                        type="time"
                        value={hours.open}
                        onChange={(e) => updateNestedSetting('hours', day, 'open', e.target.value)}
                      />
                      <span>-</span>
                      <input
                        type="time"
                        value={hours.close}
                        onChange={(e) => updateNestedSetting('hours', day, 'close', e.target.value)}
                      />
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        );

      case 'pricing':
        return (
          <div className="settings-section">
            <div className="form-group">
              <label>{t('admin.settings.baseRate')}</label>
              <input
                type="number"
                value={settings.pricing.baseRate}
                onChange={(e) => updateSetting('pricing', 'baseRate', parseFloat(e.target.value))}
                placeholder="50"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label>{t('admin.settings.urgentSurcharge')}</label>
              <input
                type="number"
                value={settings.pricing.urgentSurcharge}
                onChange={(e) => updateSetting('pricing', 'urgentSurcharge', parseFloat(e.target.value))}
                placeholder="50"
              />
            </div>

            <div className="form-group">
              <label>{t('admin.settings.minOrder')}</label>
              <input
                type="number"
                value={settings.pricing.minOrder}
                onChange={(e) => updateSetting('pricing', 'minOrder', parseFloat(e.target.value))}
                placeholder="20"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label>{t('admin.settings.taxRate')}</label>
              <input
                type="number"
                value={settings.pricing.taxRate}
                onChange={(e) => updateSetting('pricing', 'taxRate', parseFloat(e.target.value))}
                placeholder="22"
              />
            </div>
          </div>
        );

      case 'shipping':
        return (
          <div className="settings-section">
            <div className="form-group">
              <label>{t('admin.settings.freeShipping')}</label>
              <input
                type="number"
                value={settings.shipping.freeThreshold}
                onChange={(e) => updateSetting('shipping', 'freeThreshold', parseFloat(e.target.value))}
                placeholder="100"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label>{t('admin.settings.standardRate')}</label>
              <input
                type="number"
                value={settings.shipping.standardRate}
                onChange={(e) => updateSetting('shipping', 'standardRate', parseFloat(e.target.value))}
                placeholder="10"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label>{t('admin.settings.expressRate')}</label>
              <input
                type="number"
                value={settings.shipping.expressRate}
                onChange={(e) => updateSetting('shipping', 'expressRate', parseFloat(e.target.value))}
                placeholder="25"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={settings.shipping.localPickup}
                  onChange={(e) => updateSetting('shipping', 'localPickup', e.target.checked)}
                />
                {t('admin.settings.localPickup')}
              </label>
            </div>
          </div>
        );

      case 'materials':
        return (
          <div className="settings-section">
            <button onClick={addMaterial} className="admin-btn admin-btn-secondary" style={{ marginBottom: '1rem' }}>
              <Package size={16} />
              {t('admin.settings.addMaterial')}
            </button>

            {(Array.isArray(settings.materials) ? settings.materials : []).map((material, index) => (
              <div key={index} className="material-card">
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('admin.settings.materialName')}</label>
                    <input
                      type="text"
                      value={material.name}
                      onChange={(e) => updateMaterial(index, 'name', e.target.value)}
                      placeholder="PLA, ABS, PETG..."
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('admin.settings.pricePerGram')}</label>
                    <input
                      type="number"
                      value={material.pricePerGram}
                      onChange={(e) => updateMaterial(index, 'pricePerGram', parseFloat(e.target.value))}
                      placeholder="0.05"
                      step="0.01"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={material.available}
                        onChange={(e) => updateMaterial(index, 'available', e.target.checked)}
                      />
                      {t('admin.settings.available')}
                    </label>
                  </div>
                </div>

                <button 
                  onClick={() => removeMaterial(index)} 
                  className="admin-btn" 
                  style={{ background: '#ef444420', border: '1px solid #ef4444', color: '#ef4444' }}
                >
                  {t('admin.settings.remove')}
                </button>
              </div>
            ))}

            {(!Array.isArray(settings.materials) || settings.materials.length === 0) && (
              <p style={{ color: '#71717a', textAlign: 'center', padding: '2rem' }}>
                {t('admin.settings.noMaterials')}
              </p>
            )}
          </div>
        );

      case 'seo':
        return (
          <div className="settings-section">
            <div className="form-group">
              <label>{t('admin.settings.metaTitle')}</label>
              <input
                type="text"
                value={settings.seo.metaTitle}
                onChange={(e) => updateSetting('seo', 'metaTitle', e.target.value)}
                placeholder="MeccaFlex - Stampa 3D Industriale | Consegna 24h"
                maxLength={60}
              />
              <small style={{ color: '#a1a1aa' }}>{t('admin.settings.charCount', { count: `${settings.seo.metaTitle.length}/60` })}</small>
            </div>

            <div className="form-group">
              <label>{t('admin.settings.metaDescription')}</label>
              <textarea
                value={settings.seo.metaDescription}
                onChange={(e) => updateSetting('seo', 'metaDescription', e.target.value)}
                placeholder="Stampa 3D professionale per aziende. Anche un solo pezzo. Consegna rapida in 24-48h. Preventivo gratuito."
                maxLength={160}
                rows={3}
              />
              <small style={{ color: '#a1a1aa' }}>{t('admin.settings.charCount', { count: `${settings.seo.metaDescription.length}/160` })}</small>
            </div>

            <div className="form-group">
              <label>{t('admin.settings.seoKeywords')}</label>
              <div className="keywords-list">
                {(settings.seo.keywords || []).map((keyword, index) => (
                  <div key={index} className="keyword-item">
                    <input
                      type="text"
                      value={keyword}
                      onChange={(e) => updateKeyword(index, e.target.value)}
                      placeholder="stampa 3d, prototipazione..."
                    />
                    <button onClick={() => removeKeyword(index)} className="remove-btn">×</button>
                  </div>
                ))}
                <button onClick={addKeyword} className="admin-btn admin-btn-secondary">
                  {t('admin.settings.addKeyword')}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>
                <Image size={16} />
                Open Graph Image URL
              </label>
              <input
                type="url"
                value={settings.seo.ogImage}
                onChange={(e) => updateSetting('seo', 'ogImage', e.target.value)}
                placeholder="https://meccaflex.com/og-image.jpg"
              />
              <small style={{ color: '#a1a1aa' }}>{t('admin.settings.ogImageHint')}</small>
            </div>

            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>{t('admin.settings.analyticsTracking')}</h3>

            <div className="form-group">
              <label>{t('admin.settings.googleAnalyticsId')}</label>
              <input
                type="text"
                value={settings.seo.googleAnalyticsId}
                onChange={(e) => updateSetting('seo', 'googleAnalyticsId', e.target.value)}
                placeholder="G-XXXXXXXXXX"
              />
            </div>

            <div className="form-group">
              <label>{t('admin.settings.googleTagManagerId')}</label>
              <input
                type="text"
                value={settings.seo.googleTagManagerId}
                onChange={(e) => updateSetting('seo', 'googleTagManagerId', e.target.value)}
                placeholder="GTM-XXXXXXX"
              />
            </div>

            <div className="form-group">
              <label>{t('admin.settings.facebookPixelId')}</label>
              <input
                type="text"
                value={settings.seo.facebookPixelId}
                onChange={(e) => updateSetting('seo', 'facebookPixelId', e.target.value)}
                placeholder="123456789012345"
              />
            </div>
          </div>
        );

      case 'branding':
        return (
          <div className="settings-section">
            <div className="form-group">
              <label>{t('admin.settings.companyTagline')}</label>
              <input
                type="text"
                value={settings.branding.companyTagline}
                onChange={(e) => updateSetting('branding', 'companyTagline', e.target.value)}
                placeholder="Il tuo pezzo. Domani mattina."
              />
            </div>

            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>{t('admin.settings.brandColors')}</h3>

            <div className="form-row">
              <div className="form-group">
                <label>{t('admin.settings.primaryColor')}</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    value={settings.branding.primaryColor}
                    onChange={(e) => updateSetting('branding', 'primaryColor', e.target.value)}
                  />
                  <input
                    type="text"
                    value={settings.branding.primaryColor}
                    onChange={(e) => updateSetting('branding', 'primaryColor', e.target.value)}
                    placeholder="#10b981"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('admin.settings.secondaryColor')}</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    value={settings.branding.secondaryColor}
                    onChange={(e) => updateSetting('branding', 'secondaryColor', e.target.value)}
                  />
                  <input
                    type="text"
                    value={settings.branding.secondaryColor}
                    onChange={(e) => updateSetting('branding', 'secondaryColor', e.target.value)}
                    placeholder="#3b82f6"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('admin.settings.accentColor')}</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    value={settings.branding.accentColor}
                    onChange={(e) => updateSetting('branding', 'accentColor', e.target.value)}
                  />
                  <input
                    type="text"
                    value={settings.branding.accentColor}
                    onChange={(e) => updateSetting('branding', 'accentColor', e.target.value)}
                    placeholder="#f59e0b"
                  />
                </div>
              </div>
            </div>

            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>{t('admin.settings.logos')}</h3>

            <div className="form-group">
              <label>{t('admin.settings.logoUrl')}</label>
              <input
                type="url"
                value={settings.branding.logoUrl}
                onChange={(e) => updateSetting('branding', 'logoUrl', e.target.value)}
                placeholder="/logos/meccaflex-logo.svg"
              />
            </div>

            <div className="form-group">
              <label>{t('admin.settings.faviconUrl')}</label>
              <input
                type="url"
                value={settings.branding.faviconUrl}
                onChange={(e) => updateSetting('branding', 'faviconUrl', e.target.value)}
                placeholder="/favicon.ico"
              />
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="settings-section">
            <h3 style={{ marginBottom: '1rem' }}>{t('admin.settings.emailNotifications')}</h3>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={settings.notifications.emailOnNewLead}
                  onChange={(e) => updateSetting('notifications', 'emailOnNewLead', e.target.checked)}
                />
                {t('admin.settings.emailNewLead')}
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={settings.notifications.emailOnNewOrder}
                  onChange={(e) => updateSetting('notifications', 'emailOnNewOrder', e.target.checked)}
                />
                {t('admin.settings.emailNewOrder')}
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={settings.notifications.smsOnUrgentOrder}
                  onChange={(e) => updateSetting('notifications', 'smsOnUrgentOrder', e.target.checked)}
                />
                {t('admin.settings.smsUrgentOrders')}
              </label>
            </div>

            <div className="form-group">
              <label>{t('admin.settings.notificationEmail')}</label>
              <input
                type="email"
                value={settings.notifications.notificationEmail}
                onChange={(e) => updateSetting('notifications', 'notificationEmail', e.target.value)}
                placeholder="notifiche@meccaflex.com"
              />
            </div>

            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>{t('admin.settings.tabs.integrations')}</h3>

            <div className="form-group">
              <label>{t('admin.settings.slackWebhookUrl')}</label>
              <input
                type="url"
                value={settings.notifications.slackWebhookUrl}
                onChange={(e) => updateSetting('notifications', 'slackWebhookUrl', e.target.value)}
                placeholder="https://hooks.slack.com/services/..."
              />
              <small style={{ color: '#a1a1aa' }}>{t('admin.settings.slackHelp')}</small>
            </div>
          </div>
        );

      case 'security':
        return (
          <div className="settings-section">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={settings.security.twoFactorEnabled}
                  onChange={(e) => updateSetting('security', 'twoFactorEnabled', e.target.checked)}
                />
                {t('admin.settings.enable2FA')}
              </label>
            </div>

            <div className="form-group">
              <label>{t('admin.settings.sessionTimeout')}</label>
              <input
                type="number"
                value={settings.security.sessionTimeout}
                onChange={(e) => updateSetting('security', 'sessionTimeout', parseInt(e.target.value))}
                placeholder="480"
              />
              <small style={{ color: '#a1a1aa' }}>{t('admin.settings.sessionTimeoutHelp')}</small>
            </div>

            <div className="form-group">
              <label>{t('admin.settings.ipWhitelist')}</label>
              {(settings.security.ipWhitelist || []).map((ip, index) => (
                <div key={index} className="keyword-item">
                  <input
                    type="text"
                    value={ip}
                    onChange={(e) => updateListItem('security', 'ipWhitelist', index, e.target.value)}
                    placeholder="192.168.1.1"
                  />
                  <button onClick={() => removeFromList('security', 'ipWhitelist', index)} className="remove-btn">×</button>
                </div>
              ))}
              <button onClick={() => addToList('security', 'ipWhitelist')} className="admin-btn admin-btn-secondary">
                {t('admin.settings.addIP')}
              </button>
              <small style={{ color: '#a1a1aa' }}>{t('admin.settings.ipHelp')}</small>
            </div>

            <div className="form-group">
              <label>{t('admin.settings.allowedDomains')}</label>
              {(settings.security.allowedDomains || []).map((domain, index) => (
                <div key={index} className="keyword-item">
                  <input
                    type="text"
                    value={domain}
                    onChange={(e) => updateListItem('security', 'allowedDomains', index, e.target.value)}
                    placeholder="https://meccaflex.com"
                  />
                  <button onClick={() => removeFromList('security', 'allowedDomains', index)} className="remove-btn">×</button>
                </div>
              ))}
              <button onClick={() => addToList('security', 'allowedDomains')} className="admin-btn admin-btn-secondary">
                {t('admin.settings.addDomain')}
              </button>
            </div>
          </div>
        );

      case 'integrations':
        return (
          <div className="settings-section">
            <h3 style={{ marginBottom: '1rem' }}>{t('admin.settings.payments')}</h3>

            <div className="form-group">
              <label>{t('admin.settings.stripePublicKey')}</label>
              <input
                type="text"
                value={settings.integrations.stripePublicKey}
                onChange={(e) => updateSetting('integrations', 'stripePublicKey', e.target.value)}
                placeholder="pk_live_..."
              />
            </div>

            <div className="form-group">
              <label>{t('admin.settings.stripeSecretKey')}</label>
              <div style={{ padding: '0.75rem', background: '#1c1c1e', border: '1px solid #f97316', borderRadius: '8px', color: '#f97316', fontSize: '13px' }}>
                🔒 {t('admin.settings.stripeSecretWarning')}
              </div>
            </div>

            <div className="form-group">
              <label>{t('admin.settings.paypalClientId')}</label>
              <input
                type="text"
                value={settings.integrations.paypalClientId}
                onChange={(e) => updateSetting('integrations', 'paypalClientId', e.target.value)}
                placeholder="AXX..."
              />
            </div>

            <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>{t('admin.settings.communications')}</h3>

            <div className="form-group">
              <label>{t('admin.settings.whatsappBusinessId')}</label>
              <input
                type="text"
                value={settings.integrations.whatsappBusinessId}
                onChange={(e) => updateSetting('integrations', 'whatsappBusinessId', e.target.value)}
                placeholder="123456789"
              />
            </div>

            <div className="form-group">
              <label>{t('admin.settings.twilioAccountSid')}</label>
              <input
                type="text"
                value={settings.integrations.twilioAccountSid}
                onChange={(e) => updateSetting('integrations', 'twilioAccountSid', e.target.value)}
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              />
            </div>

            <div className="form-group">
              <label>{t('admin.settings.twilioAuthToken')}</label>
              <div style={{ padding: '0.75rem', background: '#1c1c1e', border: '1px solid #f97316', borderRadius: '8px', color: '#f97316', fontSize: '13px' }}>
                🔒 {t('admin.settings.twilioWarning')}
              </div>
              <small style={{ color: '#a1a1aa' }}>{t('admin.settings.smsHelp')}</small>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <header className="content-header">
          <div>
            <h1>{t('admin.settings.title')}</h1>
            <p>{t('admin.settings.subtitle')}</p>
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <LanguageToggle />
            <button onClick={loadSettings} className="admin-btn admin-btn-secondary" disabled={loading}>
              <RefreshCw size={18} className={loading ? 'spin' : ''} />
            </button>
            <button onClick={handleSave} className="admin-btn admin-btn-primary" disabled={saving}>
              {saving ? (
                <>
                  <RefreshCw size={18} className="spin" />
                  {t('admin.settings.saving')}
                </>
              ) : (
                <>
                  <Save size={18} />
                  {t('admin.settings.save')}
                </>
              )}
            </button>
          </div>
        </header>

        {successMessage && (
          <div className="success-message">
            <CheckCircle size={20} />
            <span>{successMessage}</span>
          </div>
        )}

        <div className="settings-container">
          <div className="settings-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon size={20} color={tab.color} />
                <span>{tab.label}</span>
                {dirtyTabs.has(tab.id) && (
                  <span style={{ color: '#f97316', marginLeft: 4, fontSize: 10 }}>●</span>
                )}
              </button>
            ))}
          </div>

          <div className="settings-content">
            {loading ? (
              <div style={{ padding: '3rem', textAlign: 'center' }}>
                <RefreshCw size={40} className="spin" color="#10b981" />
                <p style={{ marginTop: '1rem', color: '#a1a1aa' }}>{t('admin.common.loading')}</p>
              </div>
            ) : (
              renderTabContent()
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
